import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Submission } from '@submissions/next/models';
import { StudyDetailsFacadeService } from '@shared/study-details-facade.service';
import { of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SitesDetailFacadeService } from '@shared/services/sites-detail-facade.service';
import { EntityTypeExtend } from '@data-access/submissions';
import { organizationContactTokenizer } from '../store/tokens/organization-contact.token';

export const organizationContactResolver = (route: ActivatedRouteSnapshot) => {
  const organizationContactsStore = organizationContactTokenizer();
  const entityType: EntityTypeExtend = route.data['entityType'] ?? 'workspace';

  // Common function to setup context and load data
  const setupAndLoad = (
    entityId: string,
    entityType: EntityTypeExtend,
    options?: { relationshipId?: string; studyId?: string },
  ) => {
    if (!entityId || !entityType) return of(false);

    organizationContactsStore.setupContext(entityId, entityType, options);
    organizationContactsStore.loadOrganizationRoles();
    organizationContactsStore.loadOrganizationContact();

    return of(true);
  };

  // Determine entity type and load corresponding data
  switch (entityType) {
    // Default to 'workspace' if entity type is not specified
    case 'workspace': {
      const submission = route.parent?.data['submission'] as Submission;
      const relationshipIdRaw =
        submission.data?.['organization_affiliation_relationship_id'];
      const relationshipId =
        typeof relationshipIdRaw === 'string' && relationshipIdRaw.length > 0
          ? relationshipIdRaw
          : undefined;
      return setupAndLoad(submission.id, entityType, { relationshipId });
    }

    // Load data for 'study' entity type
    case 'study': {
      const _studyDetailFacade = inject(StudyDetailsFacadeService);
      const studyId = route.paramMap.get('studyId') ?? '';
      if (!studyId) {
        console.error(
          'organizationContactResolver: Missing studyId in route params',
        );
        return of(null);
      }

      return _studyDetailFacade.loadStudyDetailFn(studyId).pipe(
        take(1),
        switchMap((studyDetails) => {
          const id = studyDetails?.id ?? '';
          return setupAndLoad(id, entityType);
        }),
      );
    }

    // Load data for 'site' entity type
    case 'site': {
      const _siteDetailFacade = inject(SitesDetailFacadeService);
      const siteId = route.paramMap.get('siteId') ?? '';

      if (!siteId) {
        console.error(
          'organizationContactResolver: Missing siteId in route params',
        );
        return of(null);
      }

      return _siteDetailFacade.loadSiteDetailFn(siteId).pipe(
        take(1),
        switchMap((siteDetails) => {
          const id = siteDetails?.id ?? '';
          const siteStudyId = siteDetails?.study_id ?? '';
          return setupAndLoad(id, entityType, { studyId: siteStudyId });
        }),
      );
    }

    default:
      return of(false);
  }
};
